import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "labelledById", "onChange", "value", "menuPlacement", "menuPositionFixed", "as", "children", "innerRef"];
import "core-js/modules/es.array.concat.js";
import React, { createContext, useRef, useState } from 'react';
import { useItemList } from 'use-item-list';
import { KEY } from 'utils/ui/constants';
import useEventListener from 'hooks/useEventListener';
import useOnClickOutside from 'hooks/useOnClickOutside';
import usePopover from 'hooks/usePopover';
import usePopoverState from 'hooks/usePopoverState';
import useUniqueId from 'hooks/useUniqueId';
import { useTabIndexContext } from 'components/TabIndexContext';
import Button from 'components/Button';
import * as MenuListUI from 'components/MenuList';
import PopoverCard from 'components/PopoverCard';
import useMergedRefs from 'hooks/useMergedRefs';
var SelectContext = /*#__PURE__*/createContext(null);
function Select(_ref) {
  var idProp = _ref.id,
    labelledById = _ref.labelledById,
    onChange = _ref.onChange,
    value = _ref.value,
    _ref$menuPlacement = _ref.menuPlacement,
    menuPlacement = _ref$menuPlacement === void 0 ? 'bottom-start' : _ref$menuPlacement,
    menuPositionFixed = _ref.menuPositionFixed,
    as = _ref.as,
    children = _ref.children,
    innerRef = _ref.innerRef,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var buttonRef = useRef(null);
  var mergedRef = useMergedRefs([buttonRef, innerRef]);
  var popoverRef = useRef(null);
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    selectedItem = _useState2[0],
    setSelectedItem = _useState2[1];
  var id = useUniqueId(idProp);
  var tabIndexContext = useTabIndexContext();
  var ButtonComponent = as || Button;
  var popover = usePopover({
    ref: popoverRef,
    referenceRef: mergedRef,
    placement: menuPlacement,
    positionFixed: menuPositionFixed,
    adaptivePositioning: true
  });
  var _usePopoverState = usePopoverState(),
    isOpen = _usePopoverState.isOpen,
    open = _usePopoverState.open,
    close = _usePopoverState.close,
    toggle = _usePopoverState.toggle;
  var itemList = useItemList({
    id: id,
    selected: value,
    onSelect: function onSelect(selectedItem) {
      onChange(selectedItem.value);
      close();
    }
  });
  function handleGlobalMenuKeyEvents(event) {
    if (event.key === KEY.ESC) {
      event.preventDefault();
      close();
    }
  }
  function handleButtonKeyEvents(event) {
    if (event.key === KEY.ARROW_UP) {
      event.preventDefault();
      if (isOpen) {
        itemList.moveHighlightedItem(-1);
      } else {
        open();
      }
    }
    if (event.key === KEY.ARROW_DOWN) {
      event.preventDefault();
      if (isOpen) {
        itemList.moveHighlightedItem(1);
      } else {
        open();
      }
    }
    if (event.key === KEY.TAB) {
      if (isOpen) {
        itemList.selectHighlightedItem();
        close();
      }
    }
    if (event.key === KEY.SPACE || event.key === KEY.ENTER) {
      event.preventDefault();
      if (isOpen) {
        close();
        itemList.selectHighlightedItem();
      } else {
        open();
      }
    }
    if (isOpen) {
      itemList.highlightItemByString(event);
    }
  }

  // Handle global keyboard events when the menu is open
  useEventListener('keydown', handleGlobalMenuKeyEvents, {
    isEnabled: isOpen
  });

  // Close the menu when clicking outside of it
  useOnClickOutside(popoverRef, close, isOpen);
  var highlightedItemId = itemList.useHighlightedItemId() || undefined;
  return /*#__PURE__*/React.createElement(SelectContext.Provider, {
    value: {
      itemList: itemList,
      setSelectedItem: setSelectedItem
    }
  }, /*#__PURE__*/React.createElement(ButtonComponent, _extends({}, otherProps, {
    iconRight: "chevron",
    as: "div",
    role: "combobox",
    ref: popover.setReferenceRef,
    id: itemList.controllerId,
    tabIndex: tabIndexContext || 0,
    "aria-labelledby": "".concat(labelledById, " ").concat(itemList.controllerId),
    "aria-haspopup": "listbox",
    "aria-autocomplete": "none",
    "aria-controls": itemList.listId,
    "aria-activedescendant": isOpen ? highlightedItemId : undefined,
    "aria-expanded": isOpen ? 'true' : 'false',
    onKeyDown: handleButtonKeyEvents,
    onClick: toggle
  }), selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.text), /*#__PURE__*/React.createElement(PopoverCard, _extends({
    renderWhenClosed: true,
    renderInPlace: true,
    isOpen: isOpen
  }, popover.props, {
    ref: popover.setRef,
    arrow: popover.arrow
  }), /*#__PURE__*/React.createElement(MenuListUI.Wrapper, {
    role: "listbox",
    tabIndex: -1,
    id: itemList.listId,
    "aria-labelledby": labelledById
  }, children)));
}
export { SelectContext };
export default Select;